import { useCallback, useEffect, useMemo, useState } from "react"

import {
  getLeadLifecycleRules,
  saveLeadLifecycleRules
} from "./lead-lifecycle.services.ts"
import type {
  LeadLifecycleRequirement,
  LeadLifecycleRuleInput,
  RequirementMode
} from "./lead-lifecycle.types.ts"
import { readSettingsError } from "../settings-error-envelope.ts"

export interface RequirementDraft {
  from_stage: number | null
  mode: RequirementMode
  selected_fields: string[] | null
}

type DraftMap = Record<string, RequirementDraft>

const draftFromRequirement = (
  requirement: LeadLifecycleRequirement
): RequirementDraft => ({
  from_stage: requirement.from_stage,
  mode: requirement.mode,
  selected_fields: requirement.selected_fields
})

const defaultDraftFromRequirement = (
  requirement: LeadLifecycleRequirement
): RequirementDraft => ({
  from_stage: requirement.default_from_stage,
  mode: "standard",
  selected_fields: null
})

const draftsEqual = (a: RequirementDraft, b: RequirementDraft) =>
  a.from_stage === b.from_stage &&
  a.mode === b.mode &&
  JSON.stringify(a.selected_fields ?? null) ===
    JSON.stringify(b.selected_fields ?? null)



export function useLeadLifecycleRules() {
  const [requirements, setRequirements] = useState<LeadLifecycleRequirement[]>(
    []
  )
  const [drafts, setDrafts] = useState<DraftMap>({})
  const [loading, setLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    const response = await getLeadLifecycleRules()
    if (response.ok) {
      const rows = response.data?.requirements ?? []
      setRequirements(rows)
      setDrafts(
        Object.fromEntries(
          rows.map((row) => [row.key, draftFromRequirement(row)])
        )
      )
      setLoadFailed(false)
    } else {
      setLoadFailed(true)
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const dirty = useMemo(
    () =>
      requirements.some((requirement) => {
        const draft = drafts[requirement.key]
        return draft && !draftsEqual(draft, draftFromRequirement(requirement))
      }),
    [requirements, drafts]
  )

  const setStage = useCallback((key: string, from_stage: number | null) => {
    setDrafts((prev) => ({
      ...prev,
      [key]: { ...prev[key], from_stage }
    }))
  }, [])

  // The screen exposes one control per requirement — a field multi-select,
  // not a separate Standard/Specific toggle. An empty selection means
  // "use the standard built-in rule"; any selection means Specific-fields
  // mode narrowed to exactly those fields.
  const setFields = useCallback((key: string, fields: string[]) => {
    setDrafts((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        mode: fields.length > 0 ? "specific" : "standard",
        selected_fields: fields.length > 0 ? fields : null
      }
    }))
  }, [])

  const applyEntries = useCallback(
    async (entries: LeadLifecycleRuleInput[]) => {
      setSaving(true)
      setSaveError(null)
      const response = await saveLeadLifecycleRules(entries)
      if (response.ok) {
        const rows = response.data?.details?.data?.requirements ?? []
        setRequirements(rows)
        setDrafts(
          Object.fromEntries(
            rows.map((row) => [row.key, draftFromRequirement(row)])
          )
        )
      } else {
        setSaveError(readSettingsError(response.data))
      }
      setSaving(false)
      return response.ok
    },
    []
  )

  const save = useCallback(
    () =>
      applyEntries(
        requirements.map((requirement) => ({
          requirement_key: requirement.key,
          ...drafts[requirement.key]
        }))
      ),
    [requirements, drafts, applyEntries]
  )

  const resetToDefaults = useCallback(
    () =>
      applyEntries(
        requirements.map((requirement) => ({
          requirement_key: requirement.key,
          from_stage: requirement.default_from_stage,
          mode: "standard",
          selected_fields: null
        }))
      ),
    [requirements, applyEntries]
  )

  return {
    requirements,
    drafts,
    loading,
    loadFailed,
    saving,
    saveError,
    dirty,
    reload: load,
    setStage,
    setFields,
    save,
    resetToDefaults,
    defaultDraftFromRequirement
  }
}
